/**
 * Shared worlds that belong to accounts (paywall plan P2) — the app's side of apps/server/src/rooms.ts.
 *
 * Every call carries the Supabase access token. A world's files are read and written with a *room token* from
 * `/ticket` (cached here until shortly before it expires); the live connection gets a fresh 60-second ticket on
 * every (re)connect, so a changed role or a removal takes effect at once.
 */
import { accessToken } from "./auth.ts";
import { serverOrigin } from "./share.ts";

export type Role = "owner" | "admin" | "editor" | "viewer";

export interface PlanLimits {
  plan: string;
  name: string;
  source: string;
  max_worlds: number;
  max_members: number;
  storage_bytes: number;
  allow_admins: boolean;
}

export interface TicketInfo {
  ticket: string;
  token: string;
  expiresIn: { ws: number; http: number };
  role: Role;
  paused: boolean;
  world: { room_id: string; name: string; owner_id: string; owner_name: string | null; legacy: boolean };
  plan: PlanLimits;
  members: number;
  storage_bytes: number;
}

export interface MyWorld {
  room_id: string; name: string; role: Role; legacy: boolean; world_key: string | null; owner_name: string | null; last_active_at: string; paused: boolean;
  /** owner only (P4): when it was shared, the owner's "stays live" pick, storage used */
  created_at?: string; live_rank?: number | null; storage_bytes?: number | null;
}
export interface Member { user_id: string; role: Role; joined_at: string; display_name: string; color: string }
export interface MembersInfo { room_id: string; name: string; owner_id: string; owner_name: string | null; role: Role; paused: boolean; plan: PlanLimits; members: number; storage_bytes: number; owner_storage_bytes: number; list: Member[] }
export interface InviteRow { id: number; role: Role; expires_at: string; max_uses: number; uses: number; created_at: string; created_by: string }
export interface InvitePreview { status: "ok" | "invalid" | "expired" | "revoked" | "used_up"; role?: Role; world_name?: string; owner_name?: string; members?: number; max_members?: number; full?: boolean; paused?: boolean }

export class ApiError extends Error {
  status: number;
  code?: string;
  detail?: Record<string, unknown> | null;
  constructor(message: string, status: number, code?: string, detail?: Record<string, unknown> | null) {
    super(message);
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/** JSON call to the MC Planner server; signed in unless `anonymous`. */
export async function api<T>(server: string, path: string, init: RequestInit & { anonymous?: boolean } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!init.anonymous) {
    const token = await accessToken();
    if (!token) throw new ApiError("Sign in first.", 401);
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  let r: Response;
  try {
    r = await fetch(`${serverOrigin(server)}${path}`, { ...init, headers, cache: "no-store" });
  } catch {
    throw new ApiError("Can't reach the MC Planner server. Check your internet connection.", 0);
  }
  // an empty answer is fine (204); something that isn't JSON (a captive portal, a static host's index.html) is not
  const text = await r.text().catch(() => "");
  let parsed: unknown = {};
  if (text.trim()) {
    try { parsed = JSON.parse(text); } catch {
      if (r.ok) throw new ApiError("The MC Planner server sent an unexpected answer. Try again in a moment.", r.status);
      parsed = {};
    }
  }
  const body = (parsed ?? {}) as T & { error?: string; code?: string; detail?: Record<string, unknown> | null };
  if (!r.ok) throw new ApiError(body.error ?? `The server answered ${r.status}.`, r.status, body.code, body.detail);
  return body;
}

const room = (id: string) => `/api/worlds/${encodeURIComponent(id)}`;

export const myWorlds = (server: string) => api<{ worlds: MyWorld[] }>(server, "/api/worlds").then((r) => r.worlds);
/** The owner's room for this world (created the first time; the same one on every device). */
export const createWorld = (server: string, worldKey: string, name: string) =>
  api<{ room_id: string; name: string; role: Role; created: boolean; paused: boolean; plan: PlanLimits }>(server, "/api/worlds", { method: "POST", body: JSON.stringify({ worldKey, name }) });
export const deleteWorld = (server: string, id: string) => api(server, room(id), { method: "DELETE" });
export const members = (server: string, id: string) => api<MembersInfo>(server, `${room(id)}/members`);
export const setRole = (server: string, id: string, user: string, role: Role) =>
  api<MembersInfo>(server, `${room(id)}/members/${user}`, { method: "PATCH", body: JSON.stringify({ role }) });
export const removeMember = (server: string, id: string, user: string) => api(server, `${room(id)}/members/${user}`, { method: "DELETE" });
export const listInvites = (server: string, id: string) => api<{ invites: InviteRow[] }>(server, `${room(id)}/invites`).then((r) => r.invites);
export const createInvite = (server: string, id: string, opts: { role: Role; days?: number; maxUses?: number }) =>
  api<{ link: string; token: string; invite: InviteRow }>(server, `${room(id)}/invites`, { method: "POST", body: JSON.stringify(opts) });
export const revokeInvite = (server: string, id: string, invite: number) => api(server, `${room(id)}/invites/${invite}`, { method: "DELETE" });
export const invitePreview = (server: string, token: string) =>
  api<InvitePreview>(server, `/api/invites/${encodeURIComponent(token)}`, { anonymous: true }).catch((e: ApiError) =>
    e.status === 404 ? ({ status: "invalid" } as InvitePreview) : Promise.reject(e));
export const acceptInvite = (server: string, token: string) =>
  api<{ room_id: string; name: string; role: Role; owner_name: string | null; joined: boolean; paused: boolean }>(server, `/api/invites/${encodeURIComponent(token)}/accept`, { method: "POST" });
/** A world shared before accounts becomes the host's (proved with the host key from their browser). */
export const claimLegacy = (server: string, id: string, hostKey: string, worldKey: string, name: string) =>
  api<{ room_id: string; name: string; role: Role; created: boolean; paused: boolean }>(server, `${room(id)}/claim-legacy`, {
    method: "POST", headers: { "x-host-key": hostKey }, body: JSON.stringify({ worldKey, name }),
  });

// ---------------- tickets ----------------

const tokens = new Map<string, { token: string; until: number }>();
const key = (server: string, id: string) => `${serverOrigin(server)}|${id}`;

/** A fresh ticket (the WebSocket needs one per connect). Also refreshes the cached room token. */
export async function ticket(server: string, id: string): Promise<TicketInfo> {
  const t = await api<TicketInfo>(server, `${room(id)}/ticket`, { method: "POST" });
  tokens.set(key(server, id), { token: t.token, until: Date.now() + (t.expiresIn.http - 600) * 1000 });
  return t;
}

/** Headers for a room's files and images: the cached room token, renewed ten minutes before it runs out. */
export async function roomHeaders(server: string, id: string): Promise<Record<string, string>> {
  const hit = tokens.get(key(server, id));
  const token = hit && hit.until > Date.now() ? hit.token : (await ticket(server, id)).token;
  return { "x-room-token": token };
}

export function forgetTokens() {
  tokens.clear();
}

// ---------------- remembered rooms ----------------

/** worldKey → roomId of the worlds this person hosts (so the app knows the room before asking the server) */
const MAP_KEY = "mcp-rooms";
export function rememberedRoom(server: string, worldKey: string): string | null {
  try { return (JSON.parse(localStorage.getItem(MAP_KEY) ?? "{}") as Record<string, string>)[key(server, worldKey)] ?? null; } catch { return null; }
}
export function rememberRoom(server: string, worldKey: string, id: string | null) {
  try {
    const m = JSON.parse(localStorage.getItem(MAP_KEY) ?? "{}") as Record<string, string>;
    if (id) m[key(server, worldKey)] = id; else delete m[key(server, worldKey)];
    localStorage.setItem(MAP_KEY, JSON.stringify(m));
  } catch { /* private mode */ }
}

/** invite links the owner created in this browser (the server keeps only a hash, so it can't show them again) */
const LINKS_KEY = "mcp-invite-links";
export function savedLinks(id: string): Record<number, string> {
  try { return (JSON.parse(localStorage.getItem(LINKS_KEY) ?? "{}") as Record<string, Record<number, string>>)[id] ?? {}; } catch { return {}; }
}
export function saveLink(id: string, invite: number, link: string) {
  try {
    const m = JSON.parse(localStorage.getItem(LINKS_KEY) ?? "{}") as Record<string, Record<number, string>>;
    m[id] = { ...(m[id] ?? {}), [invite]: link };
    localStorage.setItem(LINKS_KEY, JSON.stringify(m));
  } catch { /* private mode */ }
}

// ---------------- invite links in the address ----------------

/** `#invite=<token>` (account worlds) */
export function parseAccountInvite(hash = location.hash): string | null {
  const t = new URLSearchParams(hash.replace(/^#/, "")).get("invite");
  return t && /^[A-Za-z0-9_-]{20,64}$/.test(t) ? t : null;
}

export const ROLE_LABEL: Record<Role, string> = { owner: "Owner", admin: "Admin", editor: "Editor", viewer: "Viewer" };
/** "412 MB", "1.5 GB", "2 GB" (no ".0") */
export const fmtBytes = (n: number) => (n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(n >= 10 * 1024 ** 3 ? 0 : 1).replace(/\.0$/, "")} GB` : `${Math.max(0, Math.round(n / 1024 ** 2))} MB`);
